import { useNavigate } from 'react-router-dom';
import type { JourneyCard as JourneyCardType, JourneyCategory } from '@/types/journey';
import { isGerman } from '@/i18n/locale';
import { Icon, IconTile } from '@/components/ui/icons';

interface Props {
  card: JourneyCardType;
  onMark: (action: 'seen' | 'completed' | 'dismissed') => void;
}

type Tone = 'primary' | 'accent' | 'warning';

const CATEGORY_META: Record<
  JourneyCategory,
  { en: string; de: string; icon: React.ReactNode; tone: Tone }
> = {
  milestone: { en: 'Milestone', de: 'Meilenstein', icon: <Icon.Sprout size={18} />, tone: 'primary' },
  tip: { en: 'Tip', de: 'Tipp', icon: <Icon.Lightbulb size={18} />, tone: 'accent' },
  expect: { en: 'What to expect', de: 'Was dich erwartet', icon: <Icon.CloudSun size={18} />, tone: 'primary' },
  reminder: { en: 'Reminder', de: 'Erinnerung', icon: <Icon.Calendar size={18} />, tone: 'warning' },
  parent_checkin: { en: 'For you', de: 'Für dich', icon: <Icon.Heart size={18} />, tone: 'accent' },
};

export function JourneyCard({ card, onMark }: Props) {
  const navigate = useNavigate();
  const de = isGerman();
  const meta = CATEGORY_META[card.category];
  const isCompleted = card.state === 'completed';
  const isSeen = card.state === 'seen' || isCompleted;

  const askAlma = () => {
    const prompt =
      card.ask_prompt ?? (de ? `Erzähl mir mehr zu: ${card.title}` : `Tell me more about: ${card.title}`);
    // Mark as seen if it wasn't already, then jump to chat with prefilled prompt.
    if (!isSeen) onMark('seen');
    navigate(`/chat?prompt=${encodeURIComponent(prompt)}`);
  };

  // For milestone cards, "completed" reads as "we've seen this".
  // For tips/reminders, "completed" reads as "done / read".
  const completeLabel =
    card.category === 'milestone'
      ? de
        ? 'Haben wir erlebt'
        : "We've seen this"
      : de
        ? 'Als gelesen markieren'
        : 'Mark as read';
  const completedLabel =
    card.category === 'milestone' ? (de ? 'Erlebt' : 'Seen it') : de ? 'Erledigt' : 'Done';

  return (
    <article className="card relative overflow-hidden" aria-label={card.title}>
      <div className="px-5 pt-4 pb-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5">
            <IconTile tone={meta.tone} size="sm">
              {meta.icon}
            </IconTile>
            <div className="text-[11px] uppercase tracking-wide font-medium text-slate-muted leading-tight">
              {de ? meta.de : meta.en}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {isSeen && !isCompleted && (
              <span className="text-[11px] text-slate-muted">{de ? 'gesehen' : 'seen'}</span>
            )}
            {!isCompleted && (
              <button
                type="button"
                onClick={() => onMark('dismissed')}
                className="icon-btn -mr-2"
                aria-label={de ? 'Karte ausblenden' : 'Hide this card'}
                title={de ? 'Karte ausblenden' : 'Hide this card'}
              >
                <Icon.X size={18} />
              </button>
            )}
          </div>
        </div>

        <h3 className="font-display text-lg font-semibold text-slate leading-snug">{card.title}</h3>
        <p className="mt-2 text-[15px] text-slate leading-relaxed">{card.body}</p>

        {card.source && (
          <p className="mt-3 text-[11px] text-slate-muted">
            {de ? 'Quelle' : 'Reference'}: {card.source}
          </p>
        )}
      </div>

      <div className="px-5 pb-4 flex flex-wrap items-center gap-2 border-t border-primary-50 pt-3">
        <button type="button" onClick={askAlma} className="btn-primary text-sm py-2 px-4">
          {de ? 'Alma fragen' : 'Ask Alma'}
        </button>
        <button
          type="button"
          onClick={() => onMark('completed')}
          disabled={isCompleted}
          aria-pressed={isCompleted}
          className="btn-secondary text-sm py-2 px-4 disabled:opacity-100 disabled:text-primary-700 disabled:border-primary-100"
        >
          {isCompleted && <Icon.Check size={16} />}
          {isCompleted ? completedLabel : completeLabel}
        </button>
      </div>
    </article>
  );
}
